import { Button } from '@monorepo/shared/componentsV2/Button';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useMoveFocus } from '@monorepo/shared/hooks/useMoveFocus';
import arrayMutators from 'final-form-arrays';
import React, { useMemo } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldConditionHelpers } from '../hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from '../types';
import { ClearAllConditionalRulesButton } from './ClearAllConditionalRulesButton';
import { ConditionalRules } from './ConditionalRules';
import { EMPTY_CONDITIONAL_RULE } from './consts';
import { isEmptyState } from './isEmptyHelpers';

export type EditFieldConditionsModalProps = {
  fieldsInGroup: EditableFormTemplateField[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (field: EditableFormTemplateField) => void;
  field: EditableFormTemplateField;
  fieldConditionHelpers: FieldConditionHelpers;
};

export function EditFieldConditionsModal({
  fieldsInGroup,
  isOpen,
  onClose,
  onSubmit,
  field,
  fieldConditionHelpers,
}: EditFieldConditionsModalProps) {
  const {
    moveFocus: moveFocusToAddRuleButton,
    focusCatcherRef: addRuleButtonRef,
  } = useMoveFocus();

  const fieldWithAtLeastOneEmptyRule = useMemo(() => {
    const defaultConditionalRules = field.conditionalRules?.length
      ? field.conditionalRules
      : [EMPTY_CONDITIONAL_RULE];
    return { ...field, conditionalRules: defaultConditionalRules };
  }, [field]);

  const submitAllowingEmptyState = async ({
    handleSubmit,
    values,
  }: {
    handleSubmit: FormRenderProps['handleSubmit'];
    values: EditableFormTemplateField;
  }) => {
    const canBypassValidation =
      !values.conditionalRules || isEmptyState(values.conditionalRules);
    if (canBypassValidation) {
      onSubmit({ ...field, conditionalRules: [] });
    } else {
      await handleSubmit();
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={fieldWithAtLeastOneEmptyRule}
      subscription={{ invalid: true, pristine: true, values: true }}
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, pristine, values }) => (
        <BaseModal
          title={`Edit conditions for ${field.name}`}
          open={isOpen}
          onClose={onClose}
          maxWidth="md"
          actionsLeft={
            <ClearAllConditionalRulesButton
              moveFocusToAddRuleButton={moveFocusToAddRuleButton}
            />
          }
          buttons={
            <>
              <Button color="primary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={pristine}
                onClick={() =>
                  submitAllowingEmptyState({ handleSubmit, values })
                }
                type="submit"
                variant="contained"
              >
                Apply
              </Button>
            </>
          }
        >
          <ErrorBoundary>
            <ConditionalRules
              addRuleButtonRef={addRuleButtonRef}
              field={field}
              fieldConditionHelpers={fieldConditionHelpers}
              moveFocusToAddRuleButton={moveFocusToAddRuleButton}
              fieldsInGroup={fieldsInGroup}
            />
          </ErrorBoundary>
        </BaseModal>
      )}
    </Form>
  );
}
